export {default as BigNumber} from 'bignumber.js';

export const unsafeIntegerSymbol = Symbol('unsafe-integer');
export const exceededMaxPrecisionFactor = Symbol('max-precision-factor');

export const MAX_DECIMAL_PLACES = 6;
export const MAX_PRECISION_FACTOR = 10 ** MAX_DECIMAL_PLACES;

export function assertSafeInteger(num: number) {
	if (!Number.isSafeInteger(num))
		throw E.user('Integer is not safe', {
			type: unsafeIntegerSymbol
		});
}

export function computeRangePositionFromK(
	k: number,
	rangeMin: number,
	rangeMax: number
) {
	const rangeSize = Math.abs(rangeMin - rangeMax);
	const min = Math.min(rangeMin, rangeMax);
	return (min + (rangeSize * k));
};

export function computeRangePositionFromPercent(
	percent: number,
	rangeMin: number,
	rangeMax: number
) {
	return (computeRangePositionFromK(percent / 100, rangeMin, rangeMax));
};

export function computeRangePercentFromPosition(
	position: number,
	rangeMin: number,
	rangeMax: number
) {
	const d = 0 - rangeMin;
	const positionFromRange = position + d;
	const length = rangeMax - rangeMin;
	return (1 / (length / positionFromRange)) * 100;
};

export function floatsEq(
	lhs: number,
	rhs: number,
	accuracity: number
) {
	return (Math.abs(lhs - rhs) < accuracity);
};

export function getClosestUpperPow2(size: number) {
	return (Math.pow(2, Math.ceil(Math.log(size) / Math.log(2))));
};

export function degToRad(deg: number) {
	return (deg * (Math.PI / 180));
};

export function radToDeg(rad: number) {
	return (rad / (Math.PI / 180));
};

export function degNormalize(deg: number) {
	while (deg < 0) {
		deg = 360 + deg;
	}
	deg = deg % 360;
	return (deg);
};

export function round(
	num: number,
	decimalPlaces: number,
	mode: 'closest' | 'up' | 'down' = 'closest'
) {
	const precisionFactor = Math.min(10 ** decimalPlaces, MAX_PRECISION_FACTOR);

	const func = (
		(mode === 'closest' && Math.round) ||
		(mode === 'up' && Math.ceil) ||
		Math.floor
	);

	num = Math.trunc(num * precisionFactor);
	assertSafeInteger(num);

	return func(num) / precisionFactor;
}

export function mod(num: number, divider: number) {
	const precisionFactor = 10 ** Math.max(countDecimalPlaces(num), countDecimalPlaces(divider));

	if (precisionFactor > MAX_PRECISION_FACTOR)
		throw E.user('Exceeded max precision factor', {
			type: exceededMaxPrecisionFactor
		});

	num = Math.round(num * precisionFactor);
	assertSafeInteger(num);

	divider = Math.round(divider * precisionFactor);
	assertSafeInteger(divider);

	const integerPart = Math.trunc(num / divider) * divider;
	assertSafeInteger(integerPart);

	return (num - integerPart) / precisionFactor;
}

export function gcd(
	lhs: number,
	rhs: number
) {
	if (Number.isNaN(lhs) || Number.isNaN(rhs)) {
		throw E.user('getTenPowerNeededToHaveFullIntegerPart : $lhs or $rhs parameter could not be NaN!', {
			lhs, rhs
		});
	}
	let r: number;
	while (rhs !== 0) {
		r = lhs % rhs;
		lhs = rhs;
		rhs = r;
	}
	return (lhs);
};

export function countDecimalPlaces(num: number) {
	const numStr = num.toString();

	const dotIdx = numStr.indexOf('.');
	const eIdx = numStr.indexOf('e');

	if (eIdx === -1) {
		if (dotIdx === -1) return 0;
		return numStr.length - dotIdx - 1;
	}

	if (numStr[eIdx + 1] === '+')
		return 0;

	if (dotIdx === -1)
		return parseInt(numStr.slice(eIdx + 2));

	return eIdx - dotIdx - 1 + parseInt(numStr.slice(eIdx + 2));
}

export function closest(
	n: number,
	possibilities: number[]
) {
	return possibilities.map<[number, number]>(x => [x, Math.abs(n - x)]).reduce(
		(a, c) => {
			return Math.min(a[1], c[1]) === a[1] ? a : c;
		},
		[Infinity, Infinity]
	)[0];
};

export function roundToStep(
	num: number,
	step: number,
	mode: 'closest' | 'up' | 'down' = 'closest'
) {
	const precisionFactor = Math.min(10 ** countDecimalPlaces(step), MAX_PRECISION_FACTOR);

	num = num * precisionFactor;

	step = Math.floor(step * precisionFactor);
	assertSafeInteger(step);

	let up = Math.ceil(num);
	assertSafeInteger(up);

	let down = Math.floor(num);
	assertSafeInteger(down);

	if (mode !== 'down') {
		const upMod = Math.abs(up % step);

		if ((up + upMod) % step === 0) {
			up += upMod;
		} else if (up - upMod > num) {
			up -= upMod;
		} else {
			up += step - upMod;
		}

		if (mode === 'up')
			return up / precisionFactor;
	}

	const downMod = Math.abs(down % step);

	if ((down - downMod) % step === 0) {
		down -= downMod;
	} else if (down + downMod < num) {
		down += downMod;
	} else {
		down -= step - downMod;
	}

	if (mode === 'down')
		return down / precisionFactor;

	if (Math.abs(num - up) <= Math.abs(num - down))
		return up / precisionFactor;

	return down / precisionFactor;
}

export function computeSizeAfterRotation(
	initialWidth: number,
	initialDepth: number,
	rotationDegree: number
) {
	const x = initialWidth;
	const z = initialDepth;
	const angleRad = degToRad(rotationDegree);
	return ({
		x: Math.abs(x * Math.sin(angleRad)) + Math.abs(z * Math.cos(angleRad)),
		z: Math.abs(x * Math.cos(angleRad)) + Math.abs(z * Math.sin(angleRad))
	});
};

export function rotatePointOnCircle(
	circleOrigin: [number, number],
	point: [number, number],
	angle: number
) {
	const newPoint = {
		x: circleOrigin[0] +
			(point[0] - circleOrigin[0]) *
			Math.cos(angle) -
			(point[1] - circleOrigin[1]) *
			Math.sin(angle),
		y: circleOrigin[1] +
			(point[0] - circleOrigin[0]) *
			Math.sin(angle) +
			(point[1] - circleOrigin[1]) *
			Math.cos(angle)
	};
	return (newPoint);
}

import * as E from './errors';
